import React, { useState, useEffect } from 'react';
import './PrivacyPolicy.css';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';

const PrivacyPolicy = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const sections = [
    { id: 'introduction', title: 'Introduction' },
    { id: 'information-collection', title: 'Information We Collect' },
    { id: 'how-we-use', title: 'How We Use Information' },
    { id: 'information-sharing', title: 'Information Sharing' },
    { id: 'data-security', title: 'Data Security' },
    { id: 'cookies', title: 'Cookies & Tracking' },
    { id: 'user-rights', title: 'Your Rights' },
    { id: 'data-retention', title: 'Data Retention' },
    { id: 'children-privacy', title: 'Children\'s Privacy' },
    { id: 'international-transfers', title: 'International Transfers' },
    { id: 'third-party-services', title: 'Third-Party Services' },
    { id: 'policy-changes', title: 'Policy Changes' },
    { id: 'contact-us', title: 'Contact Us' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  const handleScroll = () => {
    const scrollPosition = window.scrollY + 100;
    
    for (let i = sections.length - 1; i >= 0; i--) {
      const section = document.getElementById(sections[i].id);
      if (section && section.offsetTop <= scrollPosition) {
        setActiveSection(sections[i].id);
        break;
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={`pp-container ${isVisible ? 'visible' : ''}`}>
      <Navbar />
      {/* Hero Section */}
      <div className="pp-hero">
        <div className="pp-hero-content">
          <h1 className="pp-hero-title">
            <span className="pp-title-word">Privacy</span>
            <span className="pp-title-word">Policy</span>
          </h1>
          <p className="pp-hero-subtitle">
            Your privacy is important to us. Learn how we protect and handle your data.
          </p>
          <div className="pp-hero-decoration">
            <div className="pp-decoration-circle"></div>
            <div className="pp-decoration-line"></div>
            <div className="pp-decoration-circle"></div>
          </div>
        </div>
      </div>

      <div className="pp-content-wrapper">
        {/* Table of Contents */}
        <div className="pp-table-of-contents">
          <h3>Table of Contents</h3>
          <nav className="pp-toc-nav">
            {sections.map((section) => (
              <button
                key={section.id}
                className={`pp-toc-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => scrollToSection(section.id)}
              >
                {section.title}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="pp-content">
          <div className="pp-content-card">
            <section id="introduction" className="pp-section">
              <h2 className="pp-section-title">
                <span className="pp-section-number">01</span>
                Introduction
              </h2>
              <div className="pp-section-content">
                <p>
                  At WeMeetTo, we are committed to protecting your privacy and ensuring the security 
                  of your personal information. This Privacy Policy explains how we collect, use, 
                  disclose, and safeguard your information when you use our platform.
                </p>
                <p>
                  This policy applies to all users of WeMeetTo, including visitors to our website, 
                  users of our mobile applications, and participants in our services. By using 
                  WeMeetTo, you consent to the data practices described in this policy.
                </p>
                <div className="pp-highlight-box">
                  <p>
                    <strong>Last Updated:</strong> {new Date().toLocaleDateString()}
                  </p>
                </div>
              </div>
            </section>

            <section id="information-collection" className="pp-section">
              <h2 className="pp-section-title">
                <span className="pp-section-number">02</span>
                Information We Collect
              </h2>
              <div className="pp-section-content">
                <p>We collect information in several ways to provide and improve our services:</p>
                
                <div className="pp-info-grid">
                  <div className="pp-info-card">
                    <div className="pp-info-icon">👤</div>
                    <h4>Personal Information</h4>
                    <p>Name, email address, phone number, profile picture, and other information you provide when creating an account</p>
                  </div>
                  <div className="pp-info-card">
                    <div className="pp-info-icon">💬</div>
                    <h4>Communication Data</h4>
                    <p>Messages, files, and other content you share through our platform</p>
                  </div>
                  <div className="pp-info-card">
                    <div className="pp-info-icon">📱</div>
                    <h4>Device Information</h4>
                    <p>Device type, operating system, browser information, and unique device identifiers</p>
                  </div>
                  <div className="pp-info-card">
                    <div className="pp-info-icon">🌐</div>
                    <h4>Usage Data</h4>
                    <p>How you interact with our platform, features used, and time spent on the service</p>
                  </div>
                </div>

                <h4>Information You Provide</h4>
                <ul className="pp-styled-list">
                  <li>Account registration information</li>
                  <li>Profile information and preferences</li>
                  <li>Messages and communications</li>
                  <li>Files and media you upload</li>
                  <li>Feedback and support requests</li>
                </ul>

                <h4>Information We Collect Automatically</h4>
                <ul className="pp-styled-list">
                  <li>Log data (IP address, browser type, pages visited)</li>
                  <li>Device information and identifiers</li>
                  <li>Usage patterns and analytics data</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </div>
            </section>

            <section id="how-we-use" className="pp-section">
              <h2 className="pp-section-title">
                <span className="pp-section-number">03</span>
                How We Use Information
              </h2>
              <div className="pp-section-content">
                <p>We use the information we collect for various purposes to provide and improve our services:</p>
                
                <div className="pp-purpose-list">
                  <div className="pp-purpose-item">
                    <span className="pp-purpose-icon">🚀</span>
                    <div>
                      <h4>Service Delivery</h4>
                      <p>Provide, maintain, and improve our messaging and communication services</p>
                    </div>
                  </div>
                  <div className="pp-purpose-item">
                    <span className="pp-purpose-icon">🔐</span>
                    <div>
                      <h4>Security & Safety</h4>
                      <p>Protect against fraud, abuse, and ensure platform security</p>
                    </div>
                  </div>
                  <div className="pp-purpose-item">
                    <span className="pp-purpose-icon">📧</span>
                    <div>
                      <h4>Communication</h4>
                      <p>Send important updates, notifications, and respond to your inquiries</p>
                    </div>
                  </div>
                  <div className="pp-purpose-item">
                    <span className="pp-purpose-icon">📊</span>
                    <div>
                      <h4>Analytics & Improvement</h4>
                      <p>Analyze usage patterns to improve our services and user experience</p>
                    </div>
                  </div>
                </div>

                <div className="pp-highlight-box">
                  <p>
                    <strong>Legal Basis:</strong> We process your personal information based on your consent, 
                    contractual necessity, legitimate interests, and legal obligations.
                  </p>
                </div>
              </div>
            </section>

            <section id="information-sharing" className="pp-section">
              <h2 className="pp-section-title">
                <span className="pp-section-number">04</span>
                Information Sharing
              </h2>
              <div className="pp-section-content">
                <p>We do not sell, trade, or rent your personal information to third parties. We may share your information in the following limited circumstances:</p>
                
                <div className="pp-sharing-list">
                  <div className="pp-sharing-item">
                    <span className="pp-sharing-icon">👥</span>
                    <div>
                      <h4>With Your Consent</h4>
                      <p>When you explicitly agree to share information with specific parties</p>
                    </div>
                  </div>
                  <div className="pp-sharing-item">
                    <span className="pp-sharing-icon">⚖️</span>
                    <div>
                      <h4>Legal Requirements</h4>
                      <p>When required by law, court order, or to protect our rights and safety</p>
                    </div>
                  </div>
                  <div className="pp-sharing-item">
                    <span className="pp-sharing-icon">🔧</span>
                    <div>
                      <h4>Service Providers</h4>
                      <p>With trusted third-party providers who assist in operating our platform</p>
                    </div>
                  </div>
                  <div className="pp-sharing-item">
                    <span className="pp-sharing-icon">🔄</span>
                    <div>
                      <h4>Business Transfers</h4>
                      <p>In connection with mergers, acquisitions, or asset sales</p>
                    </div>
                  </div>
                </div>

                <div className="pp-prohibited-list">
                  <div className="pp-prohibited-item">
                    <span className="pp-prohibited-icon">❌</span>
                    <span>We never sell your personal information to advertisers or data brokers</span>
                  </div>
                  <div className="pp-prohibited-item">
                    <span className="pp-prohibited-icon">❌</span>
                    <span>We don't share your private messages without your explicit consent</span>
                  </div>
                  <div className="pp-prohibited-item">
                    <span className="pp-prohibited-icon">❌</span>
                    <span>We don't use your data for purposes unrelated to our service</span>
                  </div>
                </div>
              </div>
            </section>

            <section id="data-security" className="pp-section">
              <h2 className="pp-section-title">
                <span className="pp-section-number">05</span>
                Data Security
              </h2>
              <div className="pp-section-content">
                <p>
                  We implement industry-standard security measures to protect your personal information 
                  against unauthorized access, alteration, disclosure, or destruction.
                </p>
                
                <div className="pp-security-grid">
                  <div className="pp-security-card">
                    <div className="pp-security-icon">🔒</div>
                    <h4>Encryption</h4>
                    <p>End-to-end encryption for messages and data in transit</p>
                  </div>
                  <div className="pp-security-card">
                    <div className="pp-security-icon">🛡️</div>
                    <h4>Access Controls</h4>
                    <p>Strict access controls and authentication mechanisms</p>
                  </div>
                  <div className="pp-security-card">
                    <div className="pp-security-icon">🔍</div>
                    <h4>Monitoring</h4>
                    <p>Continuous monitoring and security assessments</p>
                  </div>
                  <div className="pp-security-card">
                    <div className="pp-security-icon">💾</div>
                    <h4>Secure Storage</h4>
                    <p>Secure data centers with physical and digital protection</p>
                  </div>
                </div>

                <div className="pp-highlight-box">
                  <p>
                    <strong>Your Responsibility:</strong> Please keep your account credentials secure 
                    and notify us immediately of any unauthorized access to your account.
                  </p>
                </div>
              </div>
            </section>

            <section id="cookies" className="pp-section">
              <h2 className="pp-section-title">
                <span className="pp-section-number">06</span>
                Cookies & Tracking
              </h2>
              <div className="pp-section-content">
                <p>
                  We use cookies and similar technologies to enhance your experience, analyze usage, 
                  and provide personalized content.
                </p>
                
                <div className="pp-cookie-types">
                  <div className="pp-cookie-type">
                    <h4>Essential Cookies</h4>
                    <p>Required for basic platform functionality and security</p>
                  </div>
                  <div className="pp-cookie-type">
                    <h4>Analytics Cookies</h4>
                    <p>Help us understand how users interact with our platform</p>
                  </div>
                  <div className="pp-cookie-type">
                    <h4>Preference Cookies</h4>
                    <p>Remember your settings and preferences</p>
                  </div>
                </div>

                <p>
                  You can control cookie settings through your browser preferences. However, 
                  disabling certain cookies may affect platform functionality.
                </p>
              </div>
            </section>

            <section id="user-rights" className="pp-section">
              <h2 className="pp-section-title">
                <span className="pp-section-number">07</span>
                Your Rights
              </h2>
              <div className="pp-section-content">
                <p>You have certain rights regarding your personal information:</p>
                
                <div className="pp-rights-list">
                  <div className="pp-right-item">
                    <span className="pp-right-icon">👁️</span>
                    <div>
                      <h4>Access</h4>
                      <p>Request access to your personal information we hold</p>
                    </div>
                  </div>
                  <div className="pp-right-item">
                    <span className="pp-right-icon">✏️</span>
                    <div>
                      <h4>Correction</h4>
                      <p>Request correction of inaccurate or incomplete information</p>
                    </div>
                  </div>
                  <div className="pp-right-item">
                    <span className="pp-right-icon">🗑️</span>
                    <div>
                      <h4>Deletion</h4>
                      <p>Request deletion of your personal information</p>
                    </div>
                  </div>
                  <div className="pp-right-item">
                    <span className="pp-right-icon">📤</span>
                    <div>
                      <h4>Portability</h4>
                      <p>Request a copy of your data in a portable format</p>
                    </div>
                  </div>
                  <div className="pp-right-item">
                    <span className="pp-right-icon">⏸️</span>
                    <div>
                      <h4>Restriction</h4>
                      <p>Request restriction of processing in certain circumstances</p>
                    </div>
                  </div>
                  <div className="pp-right-item">
                    <span className="pp-right-icon">🚫</span>
                    <div>
                      <h4>Objection</h4>
                      <p>Object to processing based on legitimate interests</p>
                    </div>
                  </div>
                </div>

                <p>
                  To exercise these rights, please contact us using the information provided 
                  in the "Contact Us" section below.
                </p>
              </div>
            </section>

            <section id="data-retention" className="pp-section">
              <h2 className="pp-section-title">
                <span className="pp-section-number">08</span>
                Data Retention
              </h2>
              <div className="pp-section-content">
                <p>
                  We retain your personal information only as long as necessary to fulfill the 
                  purposes outlined in this policy, unless a longer retention period is required 
                  by law.
                </p>
                
                <div className="pp-retention-info">
                  <div className="pp-retention-item">
                    <h4>Account Information</h4>
                    <p>Retained until you delete your account or request deletion</p>
                  </div>
                  <div className="pp-retention-item">
                    <h4>Messages & Communications</h4>
                    <p>Retained according to your privacy settings and legal requirements</p>
                  </div>
                  <div className="pp-retention-item">
                    <h4>Analytics Data</h4>
                    <p>Anonymized data may be retained for research and improvement purposes</p>
                  </div>
                  <div className="pp-retention-item">
                    <h4>Legal Requirements</h4>
                    <p>Some data may be retained longer to comply with legal obligations</p>
                  </div>
                </div>
              </div>
            </section>

            <section id="children-privacy" className="pp-section">
              <h2 className="pp-section-title">
                <span className="pp-section-number">09</span>
                Children's Privacy
              </h2>
              <div className="pp-section-content">
                <p>
                  WeMeetTo is not intended for children under 13 years of age. We do not 
                  knowingly collect personal information from children under 13.
                </p>
                <p>
                  If you are a parent or guardian and believe your child has provided us with 
                  personal information, please contact us immediately. We will take steps to 
                  remove such information from our systems.
                </p>
                <div className="pp-highlight-box">
                  <p>
                    <strong>Age Verification:</strong> Users between 13-18 should have parental 
                    consent before using our platform.
                  </p>
                </div>
              </div>
            </section>

            <section id="international-transfers" className="pp-section">
              <h2 className="pp-section-title">
                <span className="pp-section-number">10</span>
                International Transfers
              </h2>
              <div className="pp-section-content">
                <p>
                  Your information may be transferred to and processed in countries other than 
                  your country of residence. We ensure appropriate safeguards are in place to 
                  protect your information during such transfers.
                </p>
                <p>
                  These safeguards may include standard contractual clauses, adequacy decisions, 
                  or other legally recognized transfer mechanisms.
                </p>
              </div>
            </section>

            <section id="third-party-services" className="pp-section">
              <h2 className="pp-section-title">
                <span className="pp-section-number">11</span>
                Third-Party Services
              </h2>
              <div className="pp-section-content">
                <p>
                  Our platform may contain links to third-party websites or integrate with 
                  third-party services. This privacy policy does not apply to these external 
                  services.
                </p>
                <p>
                  We encourage you to review the privacy policies of any third-party services 
                  you choose to use or interact with through our platform.
                </p>
              </div>
            </section>

            <section id="policy-changes" className="pp-section">
              <h2 className="pp-section-title">
                <span className="pp-section-number">12</span>
                Policy Changes
              </h2>
              <div className="pp-section-content">
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in 
                  our practices or legal requirements. We will notify you of any material 
                  changes through our platform or by email.
                </p>
                <p>
                  Your continued use of WeMeetTo after any changes to this policy constitutes 
                  acceptance of the updated policy.
                </p>
                <div className="pp-highlight-box">
                  <p>
                    <strong>Notification:</strong> We will provide at least 30 days notice 
                    before implementing material changes to this policy.
                  </p>
                </div>
              </div>
            </section>

            <section id="contact-us" className="pp-section">
              <h2 className="pp-section-title">
                <span className="pp-section-number">13</span>
                Contact Us
              </h2>
              <div className="pp-section-content">
                <p>
                  If you have any questions about this Privacy Policy or our data practices, 
                  please contact us:
                </p>
                <div className="pp-contact-info">
                  <div className="pp-contact-item">
                    <span className="pp-contact-icon">📧</span>
                    <span>Email: privacy@wemeetto.com</span>
                  </div>
                  <div className="pp-contact-item">
                    <span className="pp-contact-icon">🌐</span>
                    <span>Website: www.wemeetto.com</span>
                  </div>
                  <div className="pp-contact-item">
                    <span className="pp-contact-icon">📅</span>
                    <span>Last Updated: {new Date().toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
